import React from 'react';
import { Box, Typography, Container, Button, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, CheckCircle, Star } from 'lucide-react';

const INTERNSHIP_APPLY_FORM_URL = 'https://forms.gle/LSRsjxA49uHjU8Tt5';

const INTERNSHIP_DATA: Record<
  string,
  {
    title: string;
    subtitle: string;
    image: string;
    overview: string;
    responsibilities: string[];
    skillsRequired: string[];
    goodToHave: string[];
  }
> = {
  'agentic-ai': {
    title: 'Agentic AI Intern',
    subtitle: 'LangChain & Chatbot Development',
    image: '/ai-for-everyone.jpg',
    overview:
      'We are looking for passionate interns who want to build intelligent AI systems using LLMs and automation frameworks. You will work on creating smart chatbots, AI agents, and RAG-based applications.',
    responsibilities: [
      'Build AI chatbots using LangChain',
      'Work with LLMs (GPT, LLaMA, etc.)',
      'Develop RAG (Retrieval-Augmented Generation) pipelines',
      'Integrate APIs and vector databases (FAISS, PGVector)',
      'Design prompt engineering strategies',
      'Deploy AI applications using FastAPI/Django',
    ],
    skillsRequired: [
      'Python programming',
      'Basic understanding of AI/ML concepts',
      'Knowledge of APIs & JSON',
      'Interest in LLMs & Generative AI',
    ],
    goodToHave: [
      'Experience with LangChain / OpenAI / HuggingFace',
      'Knowledge of vector databases',
      'Basic cloud knowledge (AWS/GCP)',
    ],
  },
  'physical-ai': {
    title: 'Physical AI Intern',
    subtitle: 'Robotics & Python AI',
    image: '/beyond/gallery/gallery-1.jpg',
    overview:
      'This internship focuses on building intelligent physical systems combining robotics and AI. You will work on real-world robotics projects integrated with AI-based decision-making.',
    responsibilities: [
      'Develop robotics applications using Python',
      'Work with sensors, motors, and microcontrollers',
      'Build AI-based decision systems for robots',
      'Integrate voice commands and automation',
      'Work on IoT-based smart systems',
    ],
    skillsRequired: [
      'Python programming',
      'Basic electronics & robotics knowledge',
      'Problem-solving mindset',
      'Interest in automation & AI',
    ],
    goodToHave: [
      'Arduino / Raspberry Pi experience',
      'Computer vision basics (OpenCV)',
      'Voice AI or NLP basics',
    ],
  },
};

const InternshipDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const data = id ? INTERNSHIP_DATA[id] : null;

  if (!data) {
    return (
      <Box sx={{ minHeight: '80vh', pt: 10, textAlign: 'center' }}>
        <Typography>Internship not found.</Typography>
        <Button onClick={() => navigate('/internship-hub')} sx={{ mt: 2 }}>
          Back to Internship Hub
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>
      <Container maxWidth="md" sx={{ px: 2 }}>
        {/* Back Button */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
          <Button
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/internship-hub')}
            sx={{
              color: '#2563EB',
              fontWeight: 600,
              textTransform: 'none',
              mb: 3,
              '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
            }}
          >
            Back to Internship Hub
          </Button>
        </motion.div>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Box
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              mb: 4,
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              component="img"
              src={data.image}
              alt={data.title}
              sx={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
            />
            <Box sx={{ bgcolor: '#fff', p: 3 }}>
              <Typography component="h1" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                {data.title}
              </Typography>
              <Typography sx={{ fontSize: '1rem', color: '#2563EB', fontWeight: 600 }}>{data.subtitle}</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Overview */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
            📌 Role Overview
          </Typography>
          <Typography sx={{ color: '#64748b', lineHeight: 1.8, mb: 4 }}>{data.overview}</Typography>
        </motion.div>

        {/* Responsibilities */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
          <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
            🛠️ Key Responsibilities
          </Typography>
          <Card sx={{ mb: 4, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { color: '#64748b', mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 } }}>
                {data.responsibilities.map((item) => (
                  <li key={item}>
                    <CheckCircle size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 4 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Skills Required */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
            💡 Skills Required
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
            {data.skillsRequired.map((skill) => (
              <Typography
                key={skill}
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#1e293b',
                  bgcolor: '#EAF2FF',
                  px: 2,
                  py: 1,
                  borderRadius: '10px',
                }}
              >
                {skill}
              </Typography>
            ))}
          </Box>
        </motion.div>

        {/* Good to Have */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}>
          <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
            <Star size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Good to Have
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
            {data.goodToHave.map((skill) => (
              <Typography
                key={skill}
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#64748b',
                  bgcolor: '#f1f5f9',
                  px: 2,
                  py: 1,
                  borderRadius: '10px',
                }}
              >
                {skill}
              </Typography>
            ))}
          </Box>
        </motion.div>

        {/* Internship Details */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
          <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', mb: 2 }}>
            📅 Internship Details
          </Typography>
          <Card sx={{ mb: 4, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <CardContent sx={{ p: 2.5 }}>
              {[
                { icon: '🕒', text: 'Duration: 2–6 Months' },
                { icon: '📍', text: 'Mode: Online / Hybrid' },
                { icon: '🎓', text: 'Eligibility: Students & Freshers (BCA, B.Tech, MCA, etc.)' },
                { icon: '💰', text: 'Stipend: Performance-based' },
                { icon: '📜', text: 'Certification: Yes' },
              ].map((item) => (
                <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                  <Typography sx={{ fontSize: '1.1rem' }}>{item.icon}</Typography>
                  <Typography sx={{ color: '#64748b', fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </motion.div>

        {/* Apply CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }}>
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '16px',
              p: 4,
              border: '1px solid #e5e7eb',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>How to Apply</Typography>
            <Typography sx={{ color: '#64748b', mb: 3 }}>
              Interested candidates can apply by filling out the application form or sending their resume.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component="a"
                href={INTERNSHIP_APPLY_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<Mail size={18} />}
                sx={{
                  bgcolor: '#2563EB',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  '&:hover': { bgcolor: '#1D4ED8' },
                }}
              >
                Apply Here
              </Button>
              <Button
                component="a"
                href="tel:+919716817710"
                variant="outlined"
                startIcon={<Phone size={18} />}
                sx={{
                  borderColor: '#2563EB',
                  color: '#2563EB',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  '&:hover': { borderColor: '#1D4ED8', bgcolor: 'rgba(37, 99, 235, 0.08)' },
                }}
              >
                9716817710
              </Button>
            </Box>
            <Typography sx={{ mt: 2, fontSize: '0.9rem', color: '#64748b' }}>
              Email: revivingindiaclub@gmail.com
            </Typography>
          </Box>
        </motion.div>

        {/* Back button at bottom */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate('/internship-hub')}
            sx={{
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { color: '#2563EB', bgcolor: 'rgba(37, 99, 235, 0.08)' },
            }}
          >
            Back to Internship Hub
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default InternshipDetailPage;
